/**
 * TREATX-FIPO-F8_2_C · GlobalLanguageSwitcher
 * ============================================
 *
 * Header-independent language switcher for routes that omit the main
 * <Header> chrome (full-screen flows, marketing sub-pages, embedded
 * legal viewers). Uses the SAME `useLanguage()` context as Header —
 * no parallel state, no local storage duplication (D-B11 · D-B16).
 *
 * D-B14 · D-B27 — the component depends only on the LanguageContext
 * + i18next state; no browser-only assumptions beyond React DOM. The
 * language contract itself remains the JSON PUT `/api/user/preferred-
 * language` which is web / iOS / Android agnostic.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const GlobalLanguageSwitcher = ({ variant = 'footer' }) => {
  const { language, setLanguage, enabledLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const active = enabledLanguages.find(l => l.code === language) || enabledLanguages[0];

  const isFooter = variant === 'footer';

  return (
    <div
      className="relative inline-block"
      ref={containerRef}
      data-testid="global-language-switcher"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={
          isFooter
            // The footer bottom bar sets `text-white/60`, which a child button
            // inherits — on the white footer-variant background that rendered
            // an invisible control. The text colour is therefore stated here.
            ? 'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            : 'inline-flex items-center gap-2 px-3 py-1.5 text-sm'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="global-language-selector"
      >
        <Globe className="w-4 h-4" />
        <span className="uppercase tracking-wide">{language}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[10rem] bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-72 overflow-auto"
          data-testid="global-language-menu"
        >
          {enabledLanguages.map(lang => (
            <li key={lang.code} role="option" aria-selected={lang.code === active.code}>
              <button
                type="button"
                onClick={() => { setLanguage(lang.code); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${lang.code === active.code ? 'font-semibold' : ''}`}
                data-testid={`global-language-option-${lang.code}`}
              >
                <span className="uppercase text-xs text-slate-500 mr-2">{lang.code}</span>
                {lang.nativeName}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GlobalLanguageSwitcher;
